import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Toast from './Toast';
import { registerToastApi, unregisterToastApi } from './toastBridge';
import './Toast.css';

const ToastContext = createContext(null);

const DEFAULT_DURATION_MS = 5000;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    clearTimeout(timersRef.current.get(id));
    timersRef.current.delete(id);
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((message, { type = 'info', duration = DEFAULT_DURATION_MS, description } = {}) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, message, description, type, duration }]);

    if (duration > 0) {
      timersRef.current.set(id, setTimeout(() => dismiss(id), duration));
    }

    return id;
  }, [dismiss]);

  const api = useRef({
    show,
    success: (message, options) => show(message, { ...options, type: 'success' }),
    error: (message, options) => show(message, { ...options, type: 'error' }),
    warning: (message, options) => show(message, { ...options, type: 'warning' }),
    info: (message, options) => show(message, { ...options, type: 'info' }),
    dismiss
  });
  api.current.show = show;
  api.current.dismiss = dismiss;

  useEffect(() => {
    const timers = timersRef.current;
    registerToastApi(api.current);
    return () => {
      unregisterToastApi();
      // Evita setState em provider desmontado
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      <div className="toast-viewport" role="region" aria-label="Notificações">
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} onClose={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast deve ser usado dentro de um ToastProvider');
  }
  return ctx;
};

export default ToastProvider;
